// src/pages/LoginPage.jsx

// English comments only in code blocks
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../authContext';
import { useStatus } from "../StatusContext";
import { useTranslation } from 'react-i18next';
import showPassIcon from "../medias/showpass.png";
import hidePassIcon from "../medias/hidepass.png";
import { isValidPassword } from '../../utils/validator';
import GcuAcceptation from './publicTabs/GcuAcceptation';
import discordIcon from '../../src/medias/discord-white.png';
import { bootstrapAuth } from '../../src/bootstrapAuth';
import { isBlockLoginAutoRedirect, clearBlockLoginAutoRedirect } from '../authSessionStorage';
import { navigateAfterAuthenticated } from '../utils/onboardingStorage';
import { useSecurity } from "../SecurityContext";


export default function Login() {
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const { registration, lockMessage, maintenance } = useStatus();
  const [rememberMe, setRememberMe] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const {
    injectAuth,
    isLoggedIn,
    setIsLoggedIn,
    email: authEmail,
    gcuAccepted,
    discordName,
    handles,
    setEmail: setAuthEmail,
    setMfaType: setAuthMfaType,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showRegisterButton, setShowRegisterButton] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaField, setShowMfaField] = useState(false);
  const [mfaType, setMfaType] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [showModal, setShowModal] = useState(false); // CGU modal
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isGuest } = useSecurity?.() || {};

  // ------------------------------------------------------------------
  // Redirection globale dès qu'on est loggé (Discord ou email)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isLoggedIn) return;
    if (isBlockLoginAutoRedirect()) {
      return;
    }
    navigateAfterAuthenticated(navigate, {
      email: authEmail,
      gcuAccepted,
      discordName,
      handles,
      isSuperAdmin,
      isGuest,
    });
  }, [isLoggedIn, navigate, authEmail, gcuAccepted, discordName, handles, isSuperAdmin, isGuest]);

  // Recover session when OAuth/interceptor left valid cookies but auth state was cleared.
  // Skip when blocked (auth failure / admin-lock soft logout) — cookies may still be
  // valid for another shared env and must not auto-reenter this locked env.
  useEffect(() => {
    if (isLoggedIn) return;
    if (isBlockLoginAutoRedirect()) return;

    let cancelled = false;

    (async () => {
      const { user } = await bootstrapAuth();
      if (cancelled || !user?.isLoggedIn) return;

      clearBlockLoginAutoRedirect();
      window.location.replace("/members/personalhub");
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // English comment: Discord OAuth redirect after login refusal only.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const message = params.get('message');

    if (!error) return;

    let resolvedMessage = null;

    if (error === 'registration') {
      resolvedMessage =
        message || 'Registrations are currently closed.';
    } else if (error === 'bad_state' || error === 'missing_code') {
      resolvedMessage =
        message || 'Discord login could not be verified. Please try again.';
    } else if (
      error === 'restricted' ||
      error.toLowerCase().includes('access restricted')
    ) {
      resolvedMessage =
        message ||
        (error !== 'restricted' ? error : null) ||
        'Access restricted due to current lock settings.';
    }

    if (!resolvedMessage) return;

    setAccessDeniedMessage(resolvedMessage);
    setShowEmailLogin(false);
    navigate('/login', { replace: true });
  }, [location.search, navigate]);

  const handleRedirection = () => {
    navigateAfterAuthenticated(navigate, {
      email: authEmail,
      gcuAccepted,
      discordName,
      handles,
      isSuperAdmin,
      isGuest,
    });
  };

  // ------------------------------------------------------------------
  // Discord login (inchangé)
  // ------------------------------------------------------------------
  const handleDiscordLogin = async () => {
    setAccessDeniedMessage('');

    try {
      const currentPath = window.location.pathname + window.location.search;
      const isLoginPath =
        currentPath === "/login" || currentPath.startsWith("/login/");
      const redirectTarget = isLoginPath ? "/members/personalhub" : currentPath;

      document.cookie = `initialPath=${encodeURIComponent(redirectTarget)}; path=/; SameSite=Strict`;

      const response = await axios.post('/api/auth/discord-login', {
        rememberMe: rememberMe,
      }, { withCredentials: true });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        console.error('Aucune URL de redirection reçue.');
      }
    } catch (error) {
      console.error('Erreur lors de la connexion avec Discord:', error);
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMfaCodeChange = (event) => {
    setMfaCode(event.target.value);
  };

  // ------------------------------------------------------------------
  // Effets UI existants (register, no-scroll)
  // ------------------------------------------------------------------
  useEffect(() => {
    let timer;
    if (activeTab === 'register') {
      timer = setTimeout(() => setShowPasswordField(true), 200);
    } else {
      setShowPasswordField(false);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [activeTab]);

  useEffect(() => {
    let timer;
    if (activeTab === 'register') {
      timer = setTimeout(() => setShowRegisterButton(true), 300);
    } else {
      setShowRegisterButton(false);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [activeTab]);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, []);

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  // ------------------------------------------------------------------
  // MFA handlers (email déjà dans state local)
  // ------------------------------------------------------------------
  const handleMfaLogin = async () => {
    try {
      const response = await axios.post(
        `/api/mfa/login-verify`,
        { email: email, token: mfaCode, rememberMe },
        { withCredentials: true }
      );

      if (response.data.authenticated) {
        const { user } = await bootstrapAuth();
        injectAuth(user);
        clearBlockLoginAutoRedirect();

      } else {
        setMfaError(response.data.error);
      }
    } catch (error) {
      console.error('Error during MFA verification:', error);
      setMfaError('An unexpected error occurred during MFA verification.');
    }
  };

  const handleMfaEmailLogin = async () => {
    try {
      const response = await axios.post(
        `/api/mfa-email/login-verify`,
        { email: email, token: mfaCode, rememberMe },
        { withCredentials: true }
      );

      if (response.data.message === 'MFA verified and user authenticated successfully') {
        const { user } = await bootstrapAuth();
        injectAuth(user);
        clearBlockLoginAutoRedirect();

      } else {
        setMfaError(response.data.error);
      }
    } catch (error) {
      console.error('Error during MFA email verification:', error);
      setMfaError('An unexpected error occurred during MFA email verification.');
    }
  };

  // ------------------------------------------------------------------
  // SUBMIT (login / register / reset)
  // ------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (activeTab === 'login') {
      setAccessDeniedMessage('');

      // English comment: If MFA UI is already shown → use MFA handlers.
      if (showMfaField) {
        if (mfaType === 'app') {
          handleMfaLogin();
        } else if (mfaType === 'email') {
          handleMfaEmailLogin();
        }
        return;
      }

      try {
        const response = await axios.post(
          "/api/login",
          { email: email, password: password, rememberMe },
          { withCredentials: true }
        );

        // English comment: MFA required → show MFA field.
        if (response.data.mfaRequired) {
          setMfaType(response.data.mfaType);
          setShowMfaField(true);
          return;
        }

        if (response.data.message === "MFA email sent successfully") {
          setMfaType("email");
          setShowMfaField(true);
          return;
        }

        // English comment: Login OK → now fetch full user info.
        if (response.status === 200) {

          const { user, permissions } = await bootstrapAuth();
          injectAuth(user);
          clearBlockLoginAutoRedirect();

          // English comment: AuthContext updates trigger WS connection automatically.

          navigateAfterAuthenticated(navigate, {
            email: user?.email,
            gcuAccepted: user?.gcuAccepted,
            discordName: user?.discordName,
            handles: user?.handles,
            isSuperAdmin: permissions?.isSuperAdmin ?? isSuperAdmin,
            isGuest: permissions?.isGuest ?? isGuest,
          });
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);

        if (error.response && error.response.status === 401) {
          setPasswordError("Incorrect password");
        } else if (error.response && error.response.status === 403) {
          setAccessDeniedMessage(
            error.response.data?.error ||
              'Access restricted due to current lock settings.'
          );
        } else if (error.response && error.response.status === 400) {
          setEmailError(error.response.data.error);
        } else if (!error.response) {
          console.error("Impossible to connect to the server.");
        }
      }

      return;
    }

    // -------------------------
    // Register
    // -------------------------
    if (activeTab === 'register') {
      if (!isValidPassword(password)) {
        setPasswordError(
          "Invalid password! Password must be between 3 and 30 characters and can contain alphanumeric characters and special characters."
        );
        return;
      }
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match!");
        return;
      }
      setShowModal(true);
      return;
    }

    // -------------------------
    // Reset password
    // -------------------------
    if (activeTab === 'reset') {
      try {
        const response = await axios.post(
          "/api/password/reset/request",
          { email },
          { withCredentials: true }
        );
        if (response.status === 200) {
          navigate("/confirm?status=resetlink_ok");
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          setResetMessage(error.response.data.error);
        } else if (!error.response) {
          setResetMessage("Impossible de se connecter au serveur.");
        }
      }
    }
  };


  // ------------------------------------------------------------------
  // Register (confirm modal)
  // ------------------------------------------------------------------
  const handleRegister = async () => {
    try {
      const response = await axios.post(
        `/api/register`,
        { email: email, password },
        { withCredentials: true }
      );
      if (response.data.success) {
        navigate('/confirm?status=mail_ok');
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setEmailError(error.response.data.error);
      } else if (!error.response) {
        console.error('Server is not accessible at the moment.');
      }
    }
  };

  // ------------------------------------------------------------------
  // Tab switching helpers
  // ------------------------------------------------------------------
  const goToLogin = () => {
    setActiveTab('login');
    setPassword('');
    setEmail('');
    setConfirmPassword('');
    setResetMessage('');
    setPasswordError('');
    setShowMfaField(false);
    setMfaError('');
  };

  const goToRegister = () => {
    setActiveTab('register');
    setPassword('');
    setEmail('');
    setConfirmPassword('');
    setResetMessage('');
    setPasswordError('');
    setShowMfaField(false);
    setMfaError('');
  };

  const goToReset = () => {
    setActiveTab('reset');
    setPassword('');
    setEmail('');
    setConfirmPassword('');
    setResetMessage('');
    setPasswordError('');
    setShowMfaField(false);
    setMfaError('');
  };

  const handleSwitchToEmailLogin = () => {
    setShowEmailLogin(true);
  };

  const handleSwitchToDiscordLogin = () => {
    setShowEmailLogin(false);
  };

  const isRegistrationLocked = () => {
    return registration === true;
  };

  const getLockMessage = () => {
    const hasRestriction = !!lockMessage;
    const hasRegistrationLock = registration === true;

    // English comment: No lock at all → no banner.
    if (!hasRestriction && !hasRegistrationLock) {
      return null;
    }

    // English comment: Discord-only panel → show both messages (if any) in the single lock-banner.
    if (!showEmailLogin && hasRegistrationLock) {
      return (
        <>
          {hasRestriction && (
            <>
              {lockMessage}
              <br />
            </>
          )}
          Registrations are currently closed.
        </>
      );
    }

    // English comment: Default case → keep old behavior (restriction message only).
    if (hasRestriction) {
      return lockMessage;
    }

    return null;
  };


  useEffect(() => {
    console.log("UI REACT — isLoggedIn =", isLoggedIn);
  }, [isLoggedIn]);

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div className="login-container">
      <Form
        className={`login-card login-card--${showEmailLogin ? activeTab : 'login-discord'} ${
          passwordError || resetMessage ? 'message-present' : ''
        } ${showMfaField ? 'login-card--mfa' : ''}`}
        onSubmit={handleSubmit}
      >
        <div className="login-tab-container">
          <div
            className={`login-tab${!showEmailLogin ? '-discord' : ''} ${
              activeTab === 'login' ? 'active' : ''
            }`}
            onClick={goToLogin}
          >
            {t('LoginTab')}
          </div>

          {showEmailLogin && (
            <>
              <div
                className={`login-tab ${
                  activeTab === 'register' ? 'active' : ''
                } ${isRegistrationLocked() ? 'locked' : ''}`}
                onClick={() => !isRegistrationLocked() && goToRegister()}
              >
                {t('RegisterTab')}
              </div>
              <div
                className={`login-tab ${activeTab === 'reset' ? 'active' : ''}`}
                onClick={goToReset}
              >
                {t('ResetTab')}
              </div>
            </>
          )}
        </div>

        {getLockMessage() && <div className="lock-banner">{getLockMessage()}</div>}

        <div className={`form-container ${activeTab}`}>
          {/* LOGIN */}
          {activeTab === 'login' && (
            <>
              {/* Discord mode */}
              {!showEmailLogin ? (
                <div className="discord-login">
                  <div className="login-discord-container">
                    <Button className="btn-style1 discord-button" onClick={handleDiscordLogin}>
                      <div className="icon-container icon-discord">
                        <img src={discordIcon} alt="Discord" className="icon-image" />
                      </div>
                      <label className="discord-label login-button">Login with Discord</label>
                    </Button>
                  </div>

                  <div className="custom-field remember-me discord">
                    <Form.Group controlId="formRememberMe">
                      <Form.Check
                        className={`myCheckbox discord-checkbox ${
                          showMfaField ? 'with-mfa' : ''
                        }`}
                        type="checkbox"
                        label={t('RememberMeCheckbox')}
                        checked={rememberMe}
                        onChange={handleRememberMeChange}
                      />
                    </Form.Group>
                  </div>

                  <p className="login-alternative">
                    <a className="discord-alternative" onClick={handleSwitchToEmailLogin}>
                      {t('Log with email and password instead')}
                    </a>
                  </p>
                </div>
              ) : (
                // Email/password mode
                <div className="email-login">
                  <div className="custom-field">
                    <Form.Group controlId="email">
                      <Form.Control
                        className="form-control"
                        type="text"
                        placeholder={t('Email')}
                        required
                        autoComplete="email"
                        onChange={handleEmailChange}
                        value={email}
                      />
                    </Form.Group>
                  </div>
                  <div className="custom-field password-field">
                    <div className="password-input-container">
                      <Form.Group controlId="formPassword">
                        <Form.Control
                          className="form-control"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('Password')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                        />
                        <img
                          src={showPassword ? hidePassIcon : showPassIcon}
                          alt="Afficher/Masquer le mot de passe"
                          onClick={togglePasswordVisibility}
                          className="toggle-password-icon"
                        />
                      </Form.Group>
                      <div className="error-message">
                        {passwordError && <div className="error-message">{passwordError}</div>}
                        {emailError && <div className="error-message">{emailError}</div>}
                      </div>
                    </div>
                  </div>

                  {showMfaField && (
                    <div className="custom-field mfa-field">
                      <Form.Group controlId="formMfaCode">
                        <Form.Control
                          className="mfa-field"
                          type="text"
                          placeholder="______"
                          required
                          value={mfaCode}
                          onChange={handleMfaCodeChange}
                        />
                      </Form.Group>
                      {mfaError && <div className="error-message">{mfaError}</div>}
                    </div>
                  )}

                  <div className="custom-field remember-me">
                    <Form.Group controlId="formRememberMe">
                      <Form.Check
                        className={`myCheckbox ${showMfaField ? 'with-mfa' : ''}`}
                        type="checkbox"
                        label={t('RememberMeCheckbox')}
                        checked={rememberMe}
                        onChange={handleRememberMeChange}
                      />
                    </Form.Group>
                  </div>

                  <Button
                    variant="primary"
                    type="submit"
                    className="custom-button login-button show"
                  >
                    {t('LoginBtn')}
                  </Button>

                  <p className="login-alternative">
                    <a className="discord-alternative" onClick={handleSwitchToDiscordLogin}>
                      {t('Log with Discord')}
                    </a>
                  </p>
                </div>
              )}
            </>
          )}

          {/* REGISTER */}
          {activeTab === 'register' && (
            <>
              <div className="custom-field">
                <Form.Group controlId="formEmail">
                  <Form.Control
                    className="form-control"
                    type="email"
                    placeholder={t('Email')}
                    required
                    autoComplete="email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                </Form.Group>
              </div>
              {showPasswordField && (
                <>
                  <div className="custom-field password-field">
                    <div className="password-input-container">
                      <Form.Group controlId="formPassword">
                        <Form.Control
                          className="form-control"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('Password')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        <img
                          src={showPassword ? hidePassIcon : showPassIcon}
                          alt="Toggle password visibility"
                          onClick={togglePasswordVisibility}
                          className="toggle-password-icon"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="custom-field password-field">
                    <div className="password-input-container">
                      <Form.Group controlId="formConfirmPassword">
                        <Form.Control
                          className="form-control"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('ConfirmPassword')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          isInvalid={!!passwordError}
                          autoComplete="new-password"
                        />
                      </Form.Group>
                    </div>
                    {emailError && <div className="error-message">{emailError}</div>}
                    {passwordError && <div className="error-message">{passwordError}</div>}
                  </div>
                </>
              )}
              {showRegisterButton && (
                <Button
                  variant="primary"
                  type="button"
                  className="custom-button register-button show"
                  onClick={handleRegister}
                >
                  {t('RegisterBtn')}
                </Button>
              )}
            </>
          )}

          {/* RESET */}
          {activeTab === 'reset' && (
            <>
              <div className="custom-field">
                <Form.Group controlId="formEmail">
                  <Form.Control
                    className="form-control"
                    type="email"
                    placeholder={t('Email')}
                    required
                    autoComplete="email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                </Form.Group>
              </div>
              {resetMessage && <div className="message">{resetMessage}</div>}
              <Button variant="primary" type="submit" className="custom-button register-button">
                {t('ResetPasswordBtn')}
              </Button>
            </>
          )}
        </div>
      </Form>

      {accessDeniedMessage && (
        <div className="login-access-denied" role="alert">
          {accessDeniedMessage}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        dialogClassName="gcu-modal-content"
      >
        <Modal.Header closeButton className="gcu-header">
          <Modal.Title className="gcu-title">General condition of use</Modal.Title>
        </Modal.Header>
        <Modal.Body className="gcu-content">
          <GcuAcceptation />
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            className="btn-style1"
          >
            I decline
          </Button>
          <Button variant="primary" className="btn-style1">
            I accept
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
}
